import {
    Button,
    Flex,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import type { TagType } from '@modules/product-tagging/type';
import type { Image } from '~/modules/page-builder/type/message-passing';
import ProductTaggingShell from '~/modules/product-tagging/components/product-tagging-shell/ProductTaggingShell';
import { useProductTagging } from '~/modules/product-tagging/context';
import TagPopover from '../tag-popover/TagPopover';
import * as S from './DetailSingleImageShell.style';
import HoverButtonsImage from './HoverButtonsImage';
import ImageCaptionArticle from '../../common/image-caption/ImageCaptionArticle';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { ExternalLinkLine } from '@archipro-design/icons';
import ProductTagsDisplay from '@modules/product-tagging/components/product-tags-display/ProductTagsDisplay';
import { useABTestIsOn } from '@modules/root/hook/use-growthbook';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFeatureFlaggedPaintPickerContext } from '@modules/paint-picker-new/hooks/useFeatureFlaggedPaintPickerContext';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

interface DetailSingleImageShellProps {
    children?: React.ReactNode;
    caption?: string;
    size?: string;
    className?: string;
    libraryImage?: Image;
    dimension: ColumnDimensionType;
    displayReseneTool?: boolean;
    taggingEnabled?: boolean;
    tags?: TagType[];
    uniqueApprovedTags?: TagType[];
    imageURL?: string;
    isProduct?: boolean;
    /** ImageColumn ID — needed for the boost overlay to update the column reference after upscale */
    columnId?: string;
}

const DetailSingleImageShell = ({
    children,
    caption,
    className,
    libraryImage,
    dimension,
    displayReseneTool = true,
    taggingEnabled = true,
    tags,
    uniqueApprovedTags,
    imageURL,
    isProduct = false,
    columnId,
}: DetailSingleImageShellProps) => {
    const {
        state: { isTagging },
        canCreateTag,
    } = useProductTagging();
    const isLinked = !!libraryImage?.ClickableLink || isProduct;
    const { css } = useStyles({
        dimension,
        isTagging,
        isLinked,
    });

    const {
        pickerContext: { availablePaints },
    } = useFeatureFlaggedPaintPickerContext();

    const { mobile } = useAppDisplayModeContext();

    const userCanTag = taggingEnabled && canCreateTag;

    // Dots are desktop-only. On mobile the under-image "Products tagged (N)"
    // control opens the bottom drawer — pins on the photo steal that tap.
    const showTagsOnImage =
        useABTestIsOn('product_tagging_dots') && !canCreateTag && !mobile;

    const displayTags =
        mobile && uniqueApprovedTags && uniqueApprovedTags?.length > 0;

    const onClickLinkedImage = () => {
        if (!libraryImage?.ClickableLink || isTagging) {
            return;
        }
        window.open(libraryImage.ClickableLink, '_blank');
    };

    const [showHoverButtons, setShowHoverButtons] = useState(true);

    const { ref, inView } = useInView({
        rootMargin: '2000px',
        threshold: 0,
        triggerOnce: true, // Add this to prevent re-triggering
    });

    const canBoostImage = !mobile && !!libraryImage?.ID && !!imageURL;

    const imageContainer = (
        <div
            className={`${css(S.DetailSingleImageContainer)} ${
                className || ''
            }`}
            onClick={onClickLinkedImage}
            ref={ref}
        >
            {children}
            {inView ? (
                <>
                    {taggingEnabled &&
                        libraryImage?.ID &&
                        (!mobile || userCanTag) && (
                            <ProductTaggingShell
                                libraryImage={libraryImage}
                                tags={tags}
                            />
                        )}

                    {showTagsOnImage &&
                        libraryImage &&
                        tags &&
                        tags.length > 0 && (
                            <ProductTagsDisplay
                                libraryImage={libraryImage}
                                tags={tags}
                                onToggle={(toggled) =>
                                    setShowHoverButtons(!toggled)
                                }
                            />
                        )}

                    {libraryImage?.ID && showHoverButtons && imageURL && (
                        <HoverButtonsImage
                            photoID={libraryImage.ID}
                            libraryItemId={
                                libraryImage.LibraryItemID ?? undefined
                            }
                            clickableLink={
                                libraryImage?.ClickableLink || undefined
                            }
                            imageURL={imageURL}
                            downloadLink={
                                libraryImage?.DownloadLink || undefined
                            }
                            hideReseneTool={
                                !displayReseneTool ||
                                !availablePaints ||
                                availablePaints.length === 0
                            }
                            taggingEnabled={userCanTag}
                        />
                    )}
                </>
            ) : null}
            {libraryImage?.ClickableLink && (
                <div className={css(S.DetailSingleImageLinkedIcon)}>
                    <Button
                        icon={<ExternalLinkLine />}
                        size={24}
                        color="grey"
                        as="a"
                        href={libraryImage?.ClickableLink}
                        target="_blank"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                </div>
            )}
        </div>
    );

    return (
        <>
            {canBoostImage ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImage.ID}
                    currentImageUrl={imageURL}
                    displayWidth={dimension.width}
                    displayHeight={dimension.height}
                    columnId={columnId}
                    wasUpscaled={!!libraryImage.WasUpscaled}
                    className="mx-auto w-fit"
                >
                    {imageContainer}
                </BoostResolutionOverlay>
            ) : (
                imageContainer
            )}
            {inView && (caption || displayTags) && (
                <div className={css(S.MobileCaptionContainer)}>
                    {inView && displayTags && (
                        <Flex>
                            <TagPopover
                                tags={uniqueApprovedTags}
                                dimension={dimension}
                            />
                        </Flex>
                    )}
                    {caption && (
                        <div className={css(S.ImageCaptionContainer)}>
                            <ImageCaptionArticle
                                text={caption}
                                isMain={false}
                            />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default DetailSingleImageShell;
